import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/css/style.css";

import api, { IMAGE_BASE_URL } from "../api/Api.js";
import { useLanguage } from "../context/LanguageContext";

const OurCar = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  // ============================================================
  // STATE
  // ============================================================

  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  const [expandedCar, setExpandedCar] = useState(null);

  // Car Details Popup
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [clickCount, setClickCount] = useState(0);

  // ============================================================
  // LANGUAGE ID
  // ============================================================

  const getLanguageId = () => {
    if (!language) {
      return 1;
    }

    if (typeof language === "object") {
      return Number(
        language.lang_id ||
        language.language_id ||
        language.id ||
        1
      );
    }

    return Number(language) || 1;
  };

  const languageId = getLanguageId();

  // ============================================================
  // IMAGE
  // ============================================================

  const getCarImage = (image) => {
    if (!image) {
      return "/images/car_2.jpg";
    }

    if (
      image.startsWith("http://") ||
      image.startsWith("https://")
    ) {
      return image;
    }

    if (image.startsWith("/")) {
      return `${IMAGE_BASE_URL}${image}`;
    }

    return `${IMAGE_BASE_URL}/${image}`;
  };

  // ============================================================
  // FETCH CAR LIST
  // ============================================================

  useEffect(() => {
    let cancelled = false;

    const fetchCars = async () => {
      try {
        setLoading(true);

        console.log(
          "===================================="
        );

        console.log(
          "🚗 FETCHING CAR LIST"
        );

        console.log(
          "Language ID:",
          languageId
        );

        console.log(
          "API URL:",
          "/cars/getCars"
        );

        console.log(
          "===================================="
        );

        const response = await api.get(
          `/cars/getCars?lang_id=${languageId}`
        );

        console.log(
          "🚗 CAR LIST RESPONSE:",
          response.data
        );

        if (cancelled) {
          return;
        }

        if (response.data?.success) {

          const carList =
            Array.isArray(response.data.data)
              ? response.data.data
              : [];

          console.log(
            "🚗 TOTAL CARS:",
            carList.length
          );

          setCars(carList);

        } else {

          console.error(
            "Car API returned success=false"
          );

          setCars([]);
        }

      } catch (error) {

        if (!cancelled) {

          console.error(
            "❌ CAR LIST ERROR:",
            error
          );

          console.error(
            "❌ RESPONSE:",
            error.response?.data
          );

          setCars([]);
        }

      } finally {

        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCars();

    return () => {
      cancelled = true;
    };

  }, [languageId]);


  // ============================================================
  // BOOK CAR -> goes to CarDetails.jsx
  // ============================================================

  const handleBookCar = (car) => {

    console.log(
      "🚗 SELECTED CAR:",
      car
    );

    const existingCriteria =
      sessionStorage.getItem(
        "bookingCriteria"
      );

    if (existingCriteria) {

      try {

        const criteria =
          JSON.parse(existingCriteria);

        criteria.selectedCarId =
          car.carmodel_id ||
          car.car_id ||
          car.id;

        criteria.selectedCar = car;

        sessionStorage.setItem(
          "bookingCriteria",
          JSON.stringify(criteria)
        );

      } catch (error) {

        console.error(
          "Unable to update booking criteria:",
          error
        );
      }
    }

    // Store selected car so CarDetails.jsx can resolve the carmodelId
    sessionStorage.setItem(
      "selectedCar",
      JSON.stringify(car)
    );

    navigate("/cardetails");
  };


  // ============================================================
  // SHOW MORE
  // ============================================================

  const toggleMore = (carId) => {

    setExpandedCar(
      (previous) =>
        previous === carId
          ? null
          : carId
    );
  };


  // ============================================================
  // CAR DETAILS (popup)
  // ============================================================

  const handleCarDetails = async (car) => {

    setClickCount((c) => c + 1);

    const carId =
      car.carmodel_id ||
      car.car_id ||
      car.id;

    if (!carId) {

      console.error(
        "❌ Car ID not found:",
        car
      );

      return;
    }

    console.log(
      "===================================="
    );

    console.log(
      "🚗 GETTING CAR DETAILS"
    );

    console.log(
      "Car ID:",
      carId
    );

    console.log(
      "Language ID:",
      languageId
    );

    console.log(
      "===================================="
    );

    // Open popup immediately
    setShowDetailsModal(true);

    // Show loading
    setDetailsLoading(true);

    // Clear previous data
    setSelectedCar(null);

    setDetailsError("");

    try {

      const response = await api.get(
        `/cars/${carId}/details?langid=${languageId}`
      );

      console.log(
        "🚗 CAR DETAILS RESPONSE:",
        response.data
      );

      if (response.data?.success) {

        setSelectedCar(
          response.data.data
        );

      } else {

        setDetailsError(
          response.data?.message ||
          "Car details not available."
        );
      }

    } catch (error) {

      console.error(
        "❌ CAR DETAILS ERROR:",
        error
      );

      console.error(
        "❌ DETAILS RESPONSE:",
        error.response?.data
      );

      setDetailsError(
        error.response?.data?.message ||
        "Unable to load car details."
      );

    } finally {

      setDetailsLoading(false);
    }
  };


  // ============================================================
  // CLOSE POPUP
  // ============================================================

  const closeDetailsModal = () => {

    setShowDetailsModal(false);

    setSelectedCar(null);

    setDetailsError("");
  };


  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {

    return (

      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >

        <div
          className="spinner-border"
          role="status"
        >
          <span className="visually-hidden">
            Loading...
          </span>
        </div>

      </div>
    );
  }


  // ============================================================
  // PAGE
  // ============================================================

  return (

    <>
      <div
        style={{
          background: "#ffeb3b",
          color: "#000",
          padding: "10px",
          fontWeight: "bold",
          fontFamily: "monospace",
        }}
      >
        DEBUG: cars.length = {cars.length} | loading = {String(loading)} | showDetailsModal = {String(showDetailsModal)} | clickCount = {clickCount}
      </div>

      <div className="p20">

        <div className="p0 container container2">

          <div
            className="col-md-12 main-content"
            style={{
              float: "left",
            }}
          >

            {/* ==================================================
                NO CARS
            ================================================== */}

            {cars.length === 0 ? (

              <div className="text-center py-5">

                <h4>
                  No cars available.
                </h4>

              </div>

            ) : (

              cars.map((car, index) => {

                // ==================================================
                // CAR VALUES
                // ==================================================

                const carId =
                  car.carmodel_id ||
                  car.car_id ||
                  car.id ||
                  index;

                const image =
                  getCarImage(
                    car.image
                  );

                const carName =
                  car.name ||
                  car.car_name ||
                  "Car";

                const category =
                  car.category ||
                  car.car_category ||
                  "";

                const price =
                  car.pricePerDay ??
                  car.price_per_day ??
                  car.price ??
                  0;

                const passengers =
                  car.passengers ??
                  car.persons ??
                  0;

                const luggage =
                  car.luggage ??
                  car.bags ??
                  0;

                const airco =
                  car.airco ??
                  car.air_condition ??
                  false;

                const transmission =
                  car.transmission ||
                  car.gearbox ||
                  "";

                return (

                  <div
                    className="rental-card"
                    key={carId}
                  >

                    <div className="card-main-content">

                      {/* ==================================================
                          TITLE
                      ================================================== */}

                      <div
                        className="col-xs-12 col-sm-4 col-md-9 col-lg-9"
                        style={{
                          float: "left",
                        }}
                      >

                        <h2 className="car-title">
                          {carName}
                        </h2>

                        <div className="car-subtitle">
                          {category}
                        </div>

                      </div>


                      {/* ==================================================
                          PRICE
                      ================================================== */}

                      <div
                        className="col-xs-12 col-sm-4 col-md-3 col-lg-3"
                        style={{
                          float: "left",
                        }}
                      >

                        <div className="price-container">

                          <div className="price-label">
                            (Per day)
                          </div>

                          <div className="main-price">
                            {Number(price).toFixed(2)} €
                          </div>

                        </div>

                      </div>

                      <div className="clearfix"></div>


                      {/* ==================================================
                          IMAGE
                      ================================================== */}

                      <div
                        className="col-xs-12 col-sm-4 col-md-4 col-lg-64"
                        style={{
                          float: "left",
                        }}
                      >

                        <div className="image-container">

                          <img
                            src={image}
                            alt={carName}
                            className="car-img"

                            onError={(e) => {

                              // IMPORTANT:
                              // Prevent infinite image error loop
                              if (
                                e.currentTarget.dataset.fallback ===
                                "true"
                              ) {
                                return;
                              }

                              e.currentTarget.dataset.fallback =
                                "true";

                              e.currentTarget.src =
                                "/images/car_2.jpg";
                            }}
                          />

                        </div>

                      </div>


                      {/* ==================================================
                          FEATURES
                      ================================================== */}

                      <div
                        className="col-xs-12 col-sm-4 col-md-4 col-lg-64"
                        style={{
                          float: "left",
                        }}
                      >

                        <div className="features-container">

                          {/* ==================================================
                              SPECS
                          ================================================== */}

                          <div className="specs-row">

                            <span className="spec-tag">
                              👤 {passengers}
                            </span>

                            <span className="spec-tag">
                              🧳 {luggage}
                            </span>

                            <span className="spec-tag">
                              ❄️{" "}
                              {airco
                                ? "Air-Co"
                                : "No Air-Co"}
                            </span>

                            <span className="spec-tag">
                              ⚙️{" "}
                              {transmission}
                            </span>

                          </div>


                          {/* ==================================================
                              FEATURES
                          ================================================== */}

                          <div className="feature-item">

                            <span className="feature-icon">
                              ✓
                            </span>

                            <strong>
                              No Extra Cost
                            </strong>

                          </div>


                          <div className="feature-item">

                            <span className="feature-icon">
                              ✓
                            </span>

                            <strong>
                              All inclusive
                            </strong>

                          </div>


                          <div className="feature-item">

                            <span className="feature-icon">
                              ✓
                            </span>

                            <strong>
                              No guarantee
                            </strong>

                          </div>


                          <div className="feature-item">

                            <span className="feature-icon">
                              ✓
                            </span>

                            <strong>
                              No credit card required
                            </strong>

                          </div>


                          {/* ==================================================
                              SHOW MORE
                          ================================================== */}

                          <div className="modify">

                            <button
                              type="button"
                              className="showmore"
                              onClick={() =>
                                toggleMore(carId)
                              }
                              style={{
                                border: "none",
                                background:
                                  "transparent",
                                cursor: "pointer",
                              }}
                            >

                              {expandedCar === carId
                                ? "Show Less"
                                : "Show 4 More"}

                            </button>

                          </div>


                          {/* ==================================================
                              EXTRA FEATURES
                          ================================================== */}

                          {expandedCar === carId && (

                            <div className="test1">

                              <div className="test-box1">

                                <div className="feature-item">

                                  <span className="feature-icon">
                                    ✓
                                  </span>

                                  <strong>
                                    Free roadmap of Crete
                                  </strong>

                                </div>


                                <div className="feature-item">

                                  <span className="feature-icon">
                                    ✓
                                  </span>

                                  <strong>
                                    Unlimited mileage
                                  </strong>

                                </div>


                                <div className="feature-item">

                                  <span className="feature-icon">
                                    ✓
                                  </span>

                                  <strong>
                                    24/7 road assistance
                                  </strong>

                                </div>


                                <div className="feature-item">

                                  <span className="feature-icon">
                                    ✓
                                  </span>

                                  <strong>
                                    Full insurance!
                                  </strong>

                                </div>

                              </div>

                            </div>
                          )}

                        </div>

                      </div>


                      {/* ==================================================
                          ACTIONS
                      ================================================== */}

                      <div
                        className="col-xs-12 col-sm-4 col-md-4 col-lg-64"
                        style={{
                          float: "left",
                        }}
                      >

                        <div className="actions-container">

                          {/* ==================================================
                              CAR DETAILS
                          ================================================== */}

                          <div className="car-d">

                            <button
                              type="button"

                              onClick={() =>
                                handleCarDetails(car)
                              }

                              style={{
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              Car details
                            </button>

                          </div>


                          {/* ==================================================
                              BOOK
                          ================================================== */}

                          <button
                            type="button"
                            className="book-btn"

                            onClick={() =>
                              handleBookCar(car)
                            }
                          >
                            BOOK THIS
                          </button>

                        </div>

                      </div>


                      <div className="clearfix"></div>

                    </div>

                  </div>
                );
              })
            )}

          </div>

        </div>

      </div>


      {/* ============================================================
          CAR DETAILS POPUP (overlay style, matches BookACar.jsx)
      ============================================================ */}

      {showDetailsModal && (

        <div
          className="car-details-overlay"
          onClick={closeDetailsModal}
        >

          <div
            className="car-details-modal"
            onClick={(e) => e.stopPropagation()}
          >

            <button
              type="button"
              className="car-details-close"
              onClick={closeDetailsModal}
            >
              ×
            </button>

            <h3 style={{ marginBottom: "10px" }}>
              {selectedCar?.name ||
                selectedCar?.car_name ||
                "Car Details"}
            </h3>

            {detailsLoading && (

              <div className="car-details-loading">
                Loading car details...
              </div>
            )}

            {!detailsLoading && detailsError && (

              <div className="car-details-error">
                {detailsError}
              </div>
            )}

            {!detailsLoading && selectedCar && !detailsError && (

              <>

                {/* ==================================================
                    CAR IMAGE
                ================================================== */}

                <div className="car-details-image">

                  <img
                    src={getCarImage(
                      selectedCar.image
                    )}
                    alt={
                      selectedCar.name ||
                      selectedCar.model ||
                      "Car"
                    }

                    onError={(e) => {

                      if (
                        e.currentTarget.dataset
                          .fallback === "true"
                      ) {
                        return;
                      }

                      e.currentTarget.dataset.fallback =
                        "true";

                      e.currentTarget.src =
                        "/images/car_2.jpg";
                    }}
                  />

                </div>


                {/* ==================================================
                    TABLE
                ================================================== */}

                <div className="car-details-table">

                  <CarDetailRow
                    label="Group name"
                    value={
                      selectedCar.category ||
                      selectedCar.car_category
                    }
                  />

                  <CarDetailRow
                    label="Brand name"
                    value={selectedCar.brand}
                  />

                  <CarDetailRow
                    label="Model name"
                    value={
                      selectedCar.model ||
                      selectedCar.name ||
                      selectedCar.car_name
                    }
                  />

                  <CarDetailRow
                    label="Passengers"
                    value={`X ${
                      selectedCar.passengers ??
                      selectedCar.persons ??
                      "-"
                    }`}
                  />

                  <CarDetailRow
                    label="Luggage"
                    value={
                      selectedCar.luggage ??
                      selectedCar.bags
                    }
                  />

                  <CarDetailRow
                    label="Engine capacity"
                    value={selectedCar.engineCapacity}
                  />

                  <CarDetailRow
                    label="Doors"
                    value={selectedCar.doors}
                  />

                  <CarDetailRow
                    label="Transmission"
                    value={
                      selectedCar.transmission ||
                      selectedCar.gearbox
                    }
                  />

                  <CarDetailRow
                    label="Central locking"
                    value={selectedCar.centralLocking}
                  />

                  <CarDetailRow
                    label="A.C."
                    value={
                      selectedCar.airConditioning ??
                      ((selectedCar.airco ??
                        selectedCar.air_condition)
                        ? "Yes"
                        : "No")
                    }
                  />

                  <CarDetailRow
                    label="Stereo"
                    value={selectedCar.stereo}
                  />

                  <CarDetailRow
                    label="Airbag"
                    value={selectedCar.airbag}
                  />

                  <CarDetailRow
                    label="Electric windows"
                    value={selectedCar.electricWindows}
                  />

                  <CarDetailRow
                    label="Driver age"
                    value={selectedCar.driverAge}
                  />

                  <CarDetailRow
                    label="Price Per Day"
                    value={`€ ${Number(
                      selectedCar.pricePerDay ??
                        selectedCar.price_per_day ??
                        selectedCar.price ??
                        0
                    ).toFixed(2)}`}
                  />

                </div>


                {/* ==================================================
                    ADDED ITEMS
                ================================================== */}

                <div className="car-details-added">

                  <strong>Added Items : </strong>

                  {Array.isArray(
                    selectedCar.includedItems
                  ) &&
                  selectedCar.includedItems.length > 0 ? (

                    <span>
                      {selectedCar.includedItems
                        .map((item) =>
                          item && typeof item === "object"
                            ? item.item_name ||
                              item.name ||
                              ""
                            : String(item)
                        )
                        .filter(Boolean)
                        .join(", ")}
                    </span>

                  ) : (

                    <span>N/A</span>
                  )}

                </div>


                {/* ==================================================
                    DESCRIPTION
                ================================================== */}

                {(selectedCar.description ||
                  selectedCar.details ||
                  selectedCar.content) && (

                  <div className="car-details-description">

                    {selectedCar.description ||
                      selectedCar.details ||
                      selectedCar.content}

                  </div>
                )}


                {/* ==================================================
                    BOOK FROM POPUP
                ================================================== */}

                <div
                  style={{
                    marginTop: "15px",
                    textAlign: "right",
                  }}
                >

                  <button
                    type="button"
                    className="book-btn"

                    onClick={() => {
                      closeDetailsModal();
                      handleBookCar(selectedCar);
                    }}
                  >
                    BOOK THIS
                  </button>

                </div>

              </>
            )}

            {!detailsLoading &&
              !selectedCar &&
              !detailsError && (

                <p>
                  No car details found.
                </p>
            )}

          </div>

        </div>
      )}

    </>
  );
};


// ============================================================
// DETAIL ROW (same as BookACar.jsx)
// ============================================================

function CarDetailRow({ label, value }) {
  return (
    <div className="car-detail-row">

      <div className="car-detail-label">
        {label} :
      </div>

      <div className="car-detail-value">

        {value !== undefined &&
        value !== null &&
        value !== ""
          ? typeof value === "object"
            ? "-"
            : value
          : "-"}

      </div>

    </div>
  );
}


export default OurCar;
